import {
  ClientWithSupportedProducts,
  MentionedUser,
  RemoteMentionedUser,
  RemoteUser,
  RemoteSlimUser,
  SlimUser,
  UpdateUserPayload,
  User,
  RemoteSlimUserWithRoles,
  SlimUserWithRoles,
  UpdateClientRolesParams,
} from './users.types';
import apiService from '../api/api.service';
import {mentionedUsersToClientModel, toClientUsers} from './users.dataConverters';
import apiV2Service from '../api/api-v2.service';
import _sortBy from 'lodash/sortBy';
import {fileUploadApi} from '../fileUpload/fileUpload.api';
import {AddUserParams, UpdateUserParams} from '../userClients/userClients.types';
import {users} from '@cl/shared';

const basePath = 'Users';

let activeUsersMemo: Promise<MentionedUser[]> = null;

let {userPromise, usersByIdsPromise} = users;

async function get(refreshUser = false): Promise<User> {
  if (refreshUser) {
    userPromise.current = null;
  }

  userPromise.current ??= apiV2Service
    .executeGetRequest<RemoteUser>(`${basePath}/GetCurrent`, {}, {includeClientId: false})
    .then((res) => {
      const remoteUser = res?.data;
      if (!remoteUser) return;

      const [user] = toClientUsers([remoteUser]);

      return user;
    })
    .catch((e) => {
      userPromise = null;

      throw e;
    });

  return userPromise.current;
}

async function getUsersByIds(
  usersIds: number[],
  refreshUsers = false,
): Promise<SlimUserWithRoles[]> {
  if (refreshUsers) {
    usersByIdsPromise.current = null;
  }

  usersByIdsPromise.current ??= apiV2Service
    .executePostRequest<RemoteSlimUserWithRoles[]>(`${basePath}/getusers`, undefined, {
      usersIds,
    })
    .then((result) => result.data)
    .catch((e) => {
      usersByIdsPromise = null;
      throw e;
    });

  return usersByIdsPromise.current;
}

async function getAll(): Promise<SlimUser[]> {
  const {data} = await apiV2Service.executeGetRequest<RemoteSlimUser[]>(`${basePath}`);

  return data;
}

async function getCampaignApprovers() {
  const {data} = await apiV2Service.executeGetRequest<RemoteSlimUser[]>(
    `${basePath}/GetWorkflowUsers`,
  );

  return data;
}

async function getClientUsers() {
  const {data} = await apiV2Service.executeGetRequest<RemoteSlimUser[]>(
    `${basePath}/GetClientUsers`,
  );

  return data;
}

async function updatePassword({userId, password}: {userId: number; password: string}) {
  return apiService.executePostRequest<void>(`${basePath}/UpdatePassword`, {
    userId,
    password,
  });
}

async function updateUser(userData: UpdateUserPayload) {
  const {data} = await apiV2Service.executePutRequest<void>(
    `${basePath}/${userData.userId}`,
    {},
    {
      id: userData.userId,
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email,
      isActive: userData.isActive,
      roles: userData.userRoles,
      agencyRoles: userData.agencyRoles,
      ...(userData.agencyId && {
        agency: {
          id: userData.agencyId,
        },
      }),
    },
  );

  return data;
}

async function getUserEmailByPasswordResetToken(passwordResetToken: string) {
  const {data} = await apiService.executeGetRequest<string[]>(
    `${basePath}/GetUserEmailByPasswordResetToken`,
    {passwordResetToken},
    {skipAuth: true},
  );

  const [email] = data;

  return email;
}

function getActiveUsersPerClient() {
  if (!activeUsersMemo) {
    activeUsersMemo = apiV2Service
      .executeGetRequest<RemoteMentionedUser[]>(`${basePath}/GetActiveUsersPerClient`)
      .then(({data}) => _sortBy(mentionedUsersToClientModel(data), 'lastName'));
  }

  return activeUsersMemo;
}

async function getUserClients(): Promise<ClientWithSupportedProducts[]> {
  const {data} = await apiV2Service.executeGetRequest<ClientWithSupportedProducts[]>(
    `${basePath}/GetUserClients`,
  );

  return data;
}

async function updateUserRoles(userData: UpdateUserParams): Promise<void> {
  const {data} = await apiV2Service.executePostRequest<void>(
    `${basePath}/UpdateUserRoles`,
    {},
    {
      ...userData,
    },
  );

  return data;
}

async function addUsersToAccount(userData: AddUserParams): Promise<string[]> {
  const {data} = await apiV2Service.executePutRequest<string[]>(
    `${basePath}/AddUserToAccount`,
    {},
    {
      ...userData,
    },
  );

  return data;
}

async function signUp({
  registrationToken,
  firstName,
  lastName,
  password,
  profilePictureUrl,
}: {
  registrationToken: string;
  firstName: string;
  lastName: string;
  password: string;
  profilePictureUrl?: string;
}): Promise<string> {
  const {data} = await apiV2Service.executePostRequest<string>(
    `${basePath}/SignUp`,
    {},
    {
      firstName,
      lastName,
      password,
      ...(profilePictureUrl && {profilePictureUrl}),
    },
    {
      skipAuth: true,
      includeClientId: false,
      headers: {Authorization: `Bearer ${registrationToken}`},
    },
  );

  return data;
}

async function updateUserProfile({
  firstName,
  lastName,
  profilePictureUrl,
}: {
  firstName: string;
  lastName: string;
  profilePictureUrl?: string;
}): Promise<string> {
  const {data} = await apiV2Service.executePostRequest<string>(
    `${basePath}/UpdateUserProfile`,
    {},
    {
      firstName,
      lastName,
      ...(profilePictureUrl && {profilePictureUrl}),
    },
    {includeClientId: false},
  );

  return data;
}

async function uploadProfilePicture(file: File, registrationToken?: string) {
  const {data: presignedUrl} = await apiV2Service.executeGetRequest<string>(
    `${basePath}/GetPresignedUrlForProfilePictureUpload`,
    {
      fileKey: file.name,
    },
    {
      includeClientId: false,
      ...(registrationToken && {
        skipAuth: true,
        headers: {Authorization: `Bearer ${registrationToken}`},
      }),
    },
  );

  await fileUploadApi.uploadFileToS3({
    url: presignedUrl,
    file: file,
    contentType: file.type,
  });

  const s3File = new URL(presignedUrl);

  return s3File.origin + s3File.pathname;
}

async function deleteMyUser() {
  return apiV2Service.executePostRequest<void>(
    `${basePath}/DeleteMyUser`,
    {},
    {},
    {includeClientId: false},
  );
}

async function removeUserSuspention(userId: number) {
  return apiV2Service.executePostRequest<void>(`${basePath}/UnsuspendUserClient`, {}, {userId});
}

async function updateClientRoles(params: UpdateClientRolesParams) {
  return await apiV2Service.executePostRequest<unknown>(
    `${basePath}/UpdateClientRoles`,
    {clientId: params.clientId},
    {
      clientId: params.accountId,
      updatedUserRoles: params.updatedUserRoles,
      addedUserRoles: params.addedUserRoles,
      deletedUserIds: params.deletedUserIds,
    },
  );
}

const usersApi = {
  get,
  getAll,
  updateUser,
  updatePassword,
  getUserEmailByPasswordResetToken,
  getActiveUsersPerClient,
  getUserClients,
  addUsersToAccount,
  updateUserRoles,
  signUp,
  updateUserProfile,
  uploadProfilePicture,
  deleteMyUser,
  getCampaignApprovers,
  getClientUsers,
  removeUserSuspention,
  getUsersByIds,
  updateClientRoles,
};

export default usersApi;
